/* Naughty Hut predictive search (draft theme). Takes over rendering for the
 * theme's <predictive-search> elements: fetches sections/nh-predictive.liquid,
 * shows a loading skeleton, popular searches on focus, keyboard navigation,
 * Esc to close with focus back on the input, and an aria-live result count.
 * The theme's own input handler is stopped in the capture phase so only one
 * request runs. The overlay scrim is moved below the search row so the header
 * stays crisp. Also upgrades quick-add forms on the /search page. */
(() => {
  'use strict';
  const POPULAR = ['Wand vibrator', 'Rabbit', 'Butt plug', 'Cock ring', 'Lube', 'Couples'];
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cache = new Map();

  const skeleton = () => `<div class="nhps nhps--loading" aria-hidden="true"><div class="nhps__scroll">
    <div class="nhps__group"><span class="nhps__sk nhps__sk--h"></span><div class="nhps__pills"><span class="nhps__sk nhps__sk--pill"></span><span class="nhps__sk nhps__sk--pill"></span><span class="nhps__sk nhps__sk--pill"></span></div></div>
    <div class="nhps__group"><span class="nhps__sk nhps__sk--h"></span>${'<div class="nhps__skrow"><span class="nhps__sk nhps__sk--thumb"></span><span class="nhps__sk nhps__sk--line"></span></div>'.repeat(3)}</div>
  </div></div>`;

  const idle = () => `<div class="nhps nhps--idle"><div class="nhps__scroll"><section class="nhps__group" aria-labelledby="NhpsPop">
    <h3 class="nhps__h" id="NhpsPop">Popular searches</h3>
    <ul class="nhps__pills" role="list">${POPULAR.map((p) => `<li><a class="nhps__pill" href="${root}search?q=${encodeURIComponent(p)}">${esc(p)}</a></li>`).join('')}</ul>
    <a class="nhps__quiz" href="${root}pages/find-your-match">Not sure? Take the 30-second quiz <span aria-hidden="true">→</span></a>
  </section></div></div>`;

  function setup(ps) {
    if (ps.dataset.nhps === 'on' || ps.dataset.enabled === 'false') return;
    ps.dataset.nhps = 'on';
    const input = ps.querySelector('input[type="search"]');
    const results = ps.querySelector('.search__results');
    const screen = ps.querySelector('[data-screen]');
    if (!input || !results) return;
    results.setAttribute('role', 'region');
    results.setAttribute('aria-label', 'Search suggestions');
    /* QA fix A (A11Y-11): each instance gets a unique results id and the input points at it. The field is a
       plain searchbox (type="search") with a polite live count, not a half-built combobox whose
       aria-controls named an id that did not exist. The theme element keeps its own reference. */
    results.id = `${input.id || 'Search'}-results`;
    if ('predictiveSearchResults' in ps) ps.predictiveSearchResults = results;
    /* Live fix 2026-09-30 (A11Y3-06): now that aria-controls names a real results id, the field is a full
       combobox: role, aria-autocomplete and aria-expanded (toggled by show/hide below). ArrowDown moves into the
       suggestions (keydown handler). */
    for (const a of ['aria-owns', 'aria-haspopup']) input.removeAttribute(a);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', results.id);
    const live = document.createElement('p');
    live.className = 'nhps-live visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('role', 'status');
    ps.appendChild(live);
    let ctrl = null;
    let timer = null;
    let quiet = false; // refocus after Escape must not reopen the panel
    let pressing = false; // a pointer press inside the component (Safari does not focus buttons/links on click)
    const say = (msg) => { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); };

    const place = () => {
      const row = (ps.querySelector('.search__input-wrap') || input).getBoundingClientRect();
      ps.style.setProperty('--nhps-top', `${Math.round(row.bottom + 8)}px`);
      ps.style.setProperty('--nhps-left', `${Math.round(row.left)}px`);
      ps.style.setProperty('--nhps-width', `${Math.round(row.width)}px`);
      if (screen) screen.style.top = `${Math.round(row.bottom)}px`;
    };
    const show = (html) => {
      place();
      results.innerHTML = html;
      results.style.display = 'block';
      const wasOpen = ps.classList.contains('nhps-open');
      ps.classList.add('is-active', 'nhps-open');
      input.setAttribute('aria-expanded', 'true'); // A11Y3-06
      // one overlay at a time (FE-10): the mega menu closes when suggestions open, and the reverse
      if (!wasOpen) document.dispatchEvent(new CustomEvent('nh:overlay-open', { detail: { source: 'search' } }));
    };
    const hide = (refocus) => {
      ctrl?.abort();
      results.style.display = 'none';
      results.innerHTML = '';
      ps.classList.remove('is-active', 'nhps-open');
      input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('predictive-overflow-hidden');
      if (screen) screen.style.top = '';
      if (refocus) { quiet = true; input.focus(); quiet = false; }
    };
    const run = () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (!q) { show(idle()); say(`Popular searches: ${POPULAR.length} suggestions`); return; }
      if (cache.has(q)) { render(q, cache.get(q)); return; }
      show(skeleton());
      timer = setTimeout(async () => {
        ctrl?.abort();
        ctrl = new AbortController();
        const url = `${root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=query,product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last&section_id=nh-predictive`;
        try {
          const res = await fetch(url, { signal: ctrl.signal });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const html = doc.querySelector('[data-nhps]')?.outerHTML || '';
          cache.set(q, html);
          if (input.value.trim() === q) render(q, html);
        } catch (e) {
          if (e.name !== 'AbortError') show(`<div class="nhps"><div class="nhps__scroll"><p class="nhps__empty-text">Search is taking a moment. Press Enter to see all results for “${esc(q)}”.</p></div></div>`);
        }
      }, 180);
    };
    const render = (q, html) => {
      show(html || idle());
      const n = Number(results.querySelector('[data-nhps]')?.dataset.total || 0);
      say(n ? `${n} product suggestion${n === 1 ? '' : 's'} for ${q}` : `No results for ${q}`);
    };

    // stop the theme's handler (it would fetch a second, unstyled panel)
    ps.addEventListener('input', (e) => { if (e.target === input) { e.stopImmediatePropagation(); run(); } }, true);
    input.addEventListener('focus', () => { if (!quiet && !results.innerHTML.trim()) run(); });
    /* A11Y-02: the panel and the page blur close once focus leaves the component (Tab away, tap elsewhere). */
    ps.addEventListener('pointerdown', () => { pressing = true; setTimeout(() => { pressing = false; }, 700); }, true);
    ps.addEventListener('focusout', (e) => {
      if (!ps.classList.contains('nhps-open') || pressing) return;
      if (e.relatedTarget && ps.contains(e.relatedTarget)) return;
      setTimeout(() => { if (!ps.contains(document.activeElement) && !pressing) hide(false); }, 0);
    });
    document.addEventListener('nh:overlay-open', (e) => { if (e.detail?.source !== 'search' && ps.classList.contains('nhps-open')) hide(false); });
    ps.addEventListener('keydown', (e) => {
      // preventDefault: Chrome/Safari clear a type=search field on Escape and fire an input event, which reopened the panel
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); hide(true); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = [...results.querySelectorAll('a')];
      if (!links.length) return;
      e.preventDefault();
      const i = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i < 0 ? 0 : Math.min(i + 1, links.length - 1)) : i <= 0 ? -1 : i - 1;
      if (next < 0) input.focus(); else links[next].focus();
    }, true);
    screen?.addEventListener('click', () => hide(false));
    /* FE-09: the X clears the query, closes the panel and leaves the field (it only shows while there is something to close). */
    const closeBtn = ps.querySelector('.btn--close-search');
    closeBtn?.addEventListener('mousedown', (e) => e.preventDefault());
    closeBtn?.addEventListener('click', (e) => { e.preventDefault(); e.stopImmediatePropagation(); input.value = ''; hide(false); input.blur(); say('Search cleared'); }, true);
    document.addEventListener('click', (e) => { if (ps.classList.contains('nhps-open') && !ps.contains(e.target)) hide(false); });
    window.addEventListener('resize', () => { if (ps.classList.contains('nhps-open')) place(); }, { passive: true });
    document.addEventListener('predictive-search:open', () => setTimeout(place, 120));
  }

  const init = () => document.querySelectorAll('predictive-search').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // /search page quick add (nh-p-card forms) through the theme cart drawer
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.nhs [data-nhp-atc]')) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(window.theme?.routes?.cartAdd || `${root}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'error');
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: btn }, bubbles: true }));
    } catch { form.submit(); } finally { btn?.removeAttribute('aria-busy'); }
  });
})();
/* Mobile menu drawer: always open at the top of the list (first row fully visible under the bar). */
document.addEventListener('click', (e) => {
  if (!e.target.closest || !e.target.closest('[data-nhd-open]')) return;
  requestAnimationFrame(() => document.querySelectorAll('#NhDrawer .nhd__view').forEach((v) => { v.scrollTop = 0; }));
});
/* Mobile drawer: "Find your match" chip first in the Popular row. */
(() => {
  const add = () => {
    const row = document.querySelector('#NhDrawer .nhd__chips');
    if (!row || row.querySelector('.nhd__chip--quiz')) return;
    const a = document.createElement('a');
    a.className = 'nhd__chip nhd__chip--quiz';
    a.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'pages/find-your-match';
    a.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M12 3l2 6.2L20 12l-6 2.8L12 21l-2-6.2L4 12l6-2.8z" fill="currentColor"/></svg><span>Find your match</span>';
    row.prepend(a);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', add); else add();
})();
